import { Checkbox, cn } from '@staff-app/ui';
import { formatModel, formatOs } from '@staff-app/shared-types';
import { safeDistanceToNow } from '@/lib/safe-format';
import type { MdmDevice } from '../hooks/useStaffCustomerDevices';
import { useMdmDevicesSelectionStore } from '../stores/mdm-devices-selection-store';
import { DeviceStatusDot } from './DeviceStatusDot';

// Phase 7 T070 — shared formatters take DeviceClassification.
function formatOsSafe(d: MdmDevice): string {
  return d.classification ? formatOs(d.classification) : '—';
}
function formatModelSafe(d: MdmDevice): string {
  return d.classification ? formatModel(d.classification) : (d.model ?? '—');
}

interface Props {
  devices: MdmDevice[];
  onCardClick: (deviceId: string) => void;
}

export function DeviceGrid({ devices, onCardClick }: Props) {
  const selectedIds = useMdmDevicesSelectionStore((s) => s.selectedIds);
  const toggle = useMdmDevicesSelectionStore((s) => s.toggle);

  return (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      data-testid="mdm-devices-grid"
    >
      {devices.map((device) => {
        const checked = selectedIds.has(device._id);
        return (
          <div
            key={device._id}
            data-testid="mdm-device-card"
            className={cn(
              'rounded-lg border bg-card p-3 shadow-sm transition-colors',
              'cursor-pointer hover:bg-accent/50',
              checked && 'bg-accent/30 ring-1 ring-primary/40',
            )}
            onClick={() => onCardClick(device._id)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <DeviceStatusDot device={device} />
                <div className="truncate text-sm font-medium">{device.deviceName ?? '—'}</div>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(device._id)}
                  aria-label={'Select'}
                />
              </div>
            </div>
            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
              <div className="truncate">
                {formatOsSafe(device)} · {formatModelSafe(device)}
              </div>
              <div className="truncate">
                {device.lastHeartBeat
                  ? safeDistanceToNow(device.lastHeartBeat, { addSuffix: true }, '—')
                  : '—'}
              </div>
              <div className="truncate">{device.fleetId ? device.fleetId : 'Default'}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
